import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { HttpError, api } from '../api';
import { syncLanguage, t } from '../i18n';
import { go } from '../router';
import { adoptUser } from '../session';
import { takeRecoveryReturnResult } from '../return-to';

/**
 * Owner-assisted recovery, learner half: a single-use link the owner sent
 * becomes a new password, and the learner lands back on their own home with
 * their history intact. Nothing about the account's content changes here.
 *
 * A dead link (used, expired, revoked or superseded by a newer one) always
 * reports the same thing — the server never says which — so the page offers
 * sign-in and a new link from the owner instead of a diagnosis.
 */
@customElement('recover-view')
export class RecoverView extends LitElement {
  @property() token = '';
  @state() private password = '';
  @state() private busy = false;
  @state() private error = '';
  @state() private dead = false;
  @state() private returnUnavailable = false;

  protected createRenderRoot() { return this; }

  private submit = async (e: Event) => {
    e.preventDefault();
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      const { user } = await api.recover(this.token, this.password);
      // The link is single-use; remove its bearer token from the address before rendering the success state.
      try { history.replaceState(history.state, '', '/'); } catch { /* the completion view still explains the result */ }
      // Drafts on this device now follow this account (and only this one).
      adoptUser(user);
      // The account's own interface language, before the first screen draws.
      await syncLanguage();
      const destination = takeRecoveryReturnResult(Date.now(), undefined, undefined, user.id);
      if (destination.path) go(destination.path, true);
      else this.returnUnavailable = true;
    } catch (err) {
      // 400 is about the password the learner just typed, so say so; 410 means
      // the link itself is spent and no retry will help; anything else (429
      // throttle, 5xx, network) is worth retrying, so keep the form usable.
      // HttpError messages are already in the interface language.
      if (err instanceof HttpError && err.status === 410) this.dead = true;
      else if (err instanceof HttpError) this.error = err.message;
      else this.error = t('recover.offline');
    } finally {
      this.busy = false;
    }
  };

  render() {
    return html`
      <div class="screen narrow account">
        <div class="topbar">
          <img src="/logo-64.png" class="brand-logo" alt="" width="32" height="32" />
          <h1>Eaves</h1>
        </div>
        ${this.returnUnavailable
          ? html`
              <h2>${t('recover.doneTitle')}</h2>
              <p class="muted" role="status">${t('recover.returnUnavailable')}</p>
              <button type="button" class="btn primary block" @click=${this.goHome}>${t('recover.goHome')}</button>
            `
          : this.dead
          ? html`
              <h2>${t('recover.deadTitle')}</h2>
              <p class="error" role="alert">${t('recover.deadMsg')}</p>
              <p class="muted">${t('recover.deadHelp')}</p>
              <p><a href="/signin" @click=${this.goSignin}>${t('recover.goSignin')}</a></p>
              <p class="muted small">${t('recover.askOwner')}</p>
            `
          : html`
              <h2>${t('recover.title')}</h2>
              <p class="muted">${t('recover.blurb')}</p>
              <form @submit=${this.submit}>
                <label>${t('recover.password')}
                  <input type="password" name="password" autocomplete="new-password" required minlength="10"
                    .value=${this.password} @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)} />
                </label>
                ${this.error ? html`<p class="error" role="alert">${this.error}</p>` : ''}
                <button type="submit" class="btn primary block" ?disabled=${this.busy}>${this.busy ? t('recover.saving') : t('recover.submit')}</button>
              </form>
            `}
      </div>
    `;
  }

  private goSignin = (e: Event) => {
    e.preventDefault();
    go('/signin', true);
  };

  private goHome = (e: Event) => {
    e.preventDefault();
    go('/', true);
  };
}
